import { Box, Button, HStack, Select, Stack, Text } from "@chakra-ui/react";
import InputPhone from "@raiden/library/components/Form/InputPhone";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import FormObserver from "@raiden/library/components/ReactHookForm/FormObserver";
import FormStateRHF from "@raiden/library/components/ReactHookForm/FormStateRHF";
import { RGrid } from "@raiden/library/components/RGrid";
import {
  CUSTOMERS_CONTACTS_CUSTOMER_TYPE_LIST,
  CUSTOMERS_CONTACTS_CUSTOMER_TYPE_VALUE_PARTICULAR,
} from "@raiden/library/constants/customers/contacts";
import { useApi } from "@raiden/library/hooks/useApi";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import { FormattedMessage, useIntl } from "react-intl";
import ApiErrorMessage from "../../../components/ApiErrorMessage";

/**
 * @typedef {Object} Props
 * @property {import("react-hook-form").Control<import("./Handler").ContactFormValues>} control
 * @property {boolean} isLoading
 * @property {import("@raiden/library/types/Configuration").ConfigurationCustomersContactCategory[]} customersContactCategories
 */

/**
 * @param {Props} props
 */
export function ContactsCreateForm({
  control,
  isLoading,
  customersContactCategories,
}) {
  const intl = useIntl();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Country").Country[]>} */
  const { swrResponse } = useApi(
    generateApiUrl({ id: "@api.countries.viewAny", query: { per_page: 250 } }),
  );

  const translate = useTranslate();

  return (
    <Stack spacing="1.5rem">
      <Box>
        <ApiErrorMessage withCaughtErrors={true} />

        <FormController
          control={control}
          name="data.category_id"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Quel est le sujet de votre demande ?",
          })}
          renderWithFormControl={(field) => (
            <Select
              placeholder={intl.formatMessage({
                defaultMessage: "Choisir une option",
              })}
              {...field}>
              {customersContactCategories.map((category) => (
                <option value={category.id} key={category.alias}>
                  {translate(category.name)}
                </option>
              ))}
            </Select>
          )}
        />
      </Box>

      <RGrid minCellWidth="300px" gridGap="1.5rem">
        <FormController
          control={control}
          name="data.customer_type"
          rules={{ required: true }}
          label={intl.formatMessage({ defaultMessage: "Vous êtes" })}
          renderWithFormControl={(field) => (
            <Select {...field}>
              {CUSTOMERS_CONTACTS_CUSTOMER_TYPE_LIST.map((type) => (
                <option value={type.id} key={type.id}>
                  {capitalize(intl.formatMessage(type.label))}
                </option>
              ))}
            </Select>
          )}
        />

        <FormObserver name="data.customer_type" control={control}>
          {(value) => (
            <>
              {value !== CUSTOMERS_CONTACTS_CUSTOMER_TYPE_VALUE_PARTICULAR && (
                <FormController
                  control={control}
                  name="data.company_name"
                  rules={{ required: true }}
                  label={intl.formatMessage({
                    defaultMessage: "Nom de l'entité",
                  })}
                />
              )}
            </>
          )}
        </FormObserver>
      </RGrid>

      <RGrid minCellWidth="300px" gridGap="1.5rem">
        <FormController
          control={control}
          name="data.last_name"
          rules={{ required: true }}
          label={intl.formatMessage({ defaultMessage: "Nom" })}
        />

        <FormController
          control={control}
          name="data.first_name"
          rules={{ required: true }}
          label={intl.formatMessage({ defaultMessage: "Prénom" })}
        />
      </RGrid>

      <RGrid minCellWidth="500px" gridGap="1.5rem">
        <FormController
          control={control}
          name="data.email"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Votre adresse email de contact",
          })}
        />

        <InputPhone
          countries={swrResponse.data?.data ?? []}
          nameCountry="data.phone_country"
          nameNumber="data.phone_number"
        />
      </RGrid>

      <Box>
        <FormController
          control={control}
          name="data.content"
          rules={{ required: true, maxLength: 1000 }}
          type="textarea"
          withCharactersCounter={true}
          label={intl.formatMessage({
            defaultMessage: "Comment pouvous-nous vous aider ?",
          })}
          placeholder={intl.formatMessage({
            defaultMessage: "Parlez-nous de votre projet",
          })}
        />
      </Box>

      <HStack justify="flex-end">
        <FormStateRHF
          render={({ isDirty }) => (
            <Button
              type="submit"
              colorScheme="brandPrimary"
              backgroundColor="brandPrimary.700"
              size="md"
              h="2.9375rem"
              isLoading={isLoading}
              isDisabled={!isDirty}>
              <Text variant="button" color="inherit">
                <FormattedMessage defaultMessage="Envoyer ma demande" />
              </Text>
            </Button>
          )}
        />
      </HStack>
    </Stack>
  );
}
